import { AVAILABLE_COUNTRIES } from "@/constants/campaignBuilder";

/**
 * Country homologation — turn whatever a recipient's country field holds (an ISO code, a country
 * name in any case, a common alias, blank) into a canonical ISO code from AVAILABLE_COUNTRIES.
 *
 * Manual-add already stores a code (the picker's value). Bulk upload is the messy path: a client
 * hand-types "mexico", "USA", "MX" or leaves it blank in a CSV, so we match on name OR code and
 * rewrite to the canonical code on import. Unmatched values are left as typed (so the client can
 * see and fix them); the Step 4 "Sending to" calc treats anything unresolved as US.
 */

/** code (upper) -> display name, straight from the single source of truth. */
const NAME_BY_CODE = new Map(AVAILABLE_COUNTRIES.map((c) => [c.code, c.name]));

/** lower-cased canonical name -> code. */
const CODE_BY_NAME = new Map(AVAILABLE_COUNTRIES.map((c) => [c.name.toLowerCase(), c.code]));

/**
 * Extra spellings that don't equal a canonical AVAILABLE_COUNTRIES name. Two come from our own list
 * preferring a short form (US is "USA", CZ is "Czechia"); the rest are the informal names a client
 * is likely to type in a spreadsheet. Keys are lower-cased.
 */
const ALIASES: Record<string, string> = {
  "united states": "US",
  "united states of america": "US",
  "u.s.": "US",
  "u.s.a.": "US",
  "u.s.a": "US",
  america: "US",
  uk: "GB",
  "u.k.": "GB",
  "great britain": "GB",
  britain: "GB",
  england: "GB",
  "czech republic": "CZ",
  "south korea": "KR",
  korea: "KR",
  "republic of korea": "KR",
  "north korea": "KP",
  uae: "AE",
  "u.a.e.": "AE",
  emirates: "AE",
  "ivory coast": "CI",
  holland: "NL",
  russia: "RU",
  "russian federation": "RU",
};

/**
 * Resolve an arbitrary country string to a canonical ISO code, or null if nothing matches.
 * Order: exact code → canonical name → alias. Case- and whitespace-insensitive.
 */
export function matchCountryCode(input: string | null | undefined): string | null {
  const raw = (input ?? "").trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (NAME_BY_CODE.has(upper)) return upper; // already a valid code
  const lower = raw.toLowerCase();
  return CODE_BY_NAME.get(lower) ?? ALIASES[lower] ?? null;
}

/** Canonical display name for a code, or undefined if the code is unknown. */
export function countryNameFromCode(code: string | null | undefined): string | undefined {
  return code ? NAME_BY_CODE.get(code.trim().toUpperCase()) : undefined;
}

/**
 * The country a recipient counts as for shipping/reach calculations: the matched code, or "US" when
 * the value is blank, unmapped, or otherwise unresolved (an empty cell defaults to US).
 */
export function resolveCountryCodeOrUs(input: string | null | undefined): string {
  return matchCountryCode(input) ?? "US";
}
